import React, { createContext, useContext, useMemo } from 'react';
import type { EditorScreen } from '@/store/editorSlice';

/**
 * WEB-1012 — «какая воронка меня окружает» для блоков, которым мало своих полей.
 *
 * Экран загрузки обязан называть стадии по секциям воронки и подхватывать её
 * отзывы, но блок видит только сам себя. Тянуть экраны пропсом через
 * PublicBlockRenderer нельзя: у него десяток вызывающих мест, и каждое
 * забытое молча вернуло бы блок к зашитому тексту.
 *
 * Провайдер вешают визарды (QuizScreenWizard / PaywallScreenWizard) — а через
 * них идут И публичная страница, И превью конструктора (шрам WEB-953: превью
 * и паблик обязаны быть одним кодом, копия расходится молча).
 *
 * Дефолт — пустая воронка, то есть поведение ровно до этого атома: блок вне
 * визарда (карточка блока, hover-превью) рисуется как раньше.
 */
export interface FunnelContentContextValue {
  screens: readonly EditorScreen[];
  /**
   * Слово владельца 21.10.2026: вид загрузчика своими полями воронки
   * (`settings.quizLoader`, сырое значение — разбирает `resolveQuizLoader`).
   * Блок загрузчика видит только себя, поэтому поле воронки едет сюда же.
   */
  quizLoader?: unknown;
}

const EMPTY_SCREENS: readonly EditorScreen[] = [];

const FunnelContentContext = createContext<FunnelContentContextValue>({
  screens: EMPTY_SCREENS,
});

export function FunnelContentProvider({
  screens,
  quizLoader,
  children,
}: {
  screens: readonly EditorScreen[];
  quizLoader?: unknown;
  children: React.ReactNode;
}): React.ReactElement {
  const value = useMemo<FunnelContentContextValue>(
    () => ({ screens, quizLoader }),
    [screens, quizLoader],
  );
  return <FunnelContentContext.Provider value={value}>{children}</FunnelContentContext.Provider>;
}

export function useFunnelContent(): FunnelContentContextValue {
  return useContext(FunnelContentContext);
}
